import classNames from 'classnames';
import React, {useEffect} from 'react';
import type {WithRouterProps} from 'react-router';

import appActions from 'client/AppActions';
import {callAction} from 'client/framework';
import ClassroomInvitations from './ClassroomInvitations/ClassroomInvitations';
import {
  HardCapStudentErrorModal,
  InvalidEmailModal
} from 'client/Classrooms/StudentDashboard/Modals';
import Header from './Header';
import isWelcomeRoute from './isWelcomeRoute';
import JoinClassroomModal from 'client/Classrooms/JoinClassroom/JoinClassroomModal';
import {LoadingSpinner} from '@albert-io/atomic';
import SidebarContent from './Sidebar/SidebarContent';
import SidebarWrapperDesktop from './Sidebar/SidebarWrapperDesktop';
import SidebarWrapperMobile from './Sidebar/SidebarWrapperMobile';
import Tabs from './Tabs';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import useOnClassroomEnrollment from './hooks/useOnClassroomEnrollment';
import usePrevious from 'lib/hooks/usePrevious';
import useStudent from './hooks/useStudent';
import {useStudentHomepageStore} from './useStudentHomepageStore';
import useSyncDefaultRoute from './hooks/useSyncDefaultRoute';

import {TRANSITION_DELAY_CLASSNAME, TRANSITION_DURATION_CLASSNAME} from './transitions';

const footerActions = appActions as Record<'SET_FOOTER_DISPLAY', symbol>;

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location, params}: Props) {
  'use memo';

  const {isSAndDown} = useBreakpoint();
  const lastIsSAnddown = usePrevious(isSAndDown);

  const {classroomId} = params;
  const {pathname} = location;

  const {fetchStudent, isStudentLoading} = useStudent();
  const onClassroomEnrollment = useOnClassroomEnrollment();

  const countClassrooms = useStudentHomepageStore((state) => state.classrooms.size);
  const exceededHardCapLimit = useStudentHomepageStore((state) => state.exceededHardCapLimit);
  const hasInvitations = useStudentHomepageStore((state) => state.invitations.size > 0);
  const isMaybeRedirectingUser = useStudentHomepageStore((state) => state.userDefaultRoute === null);
  const isSidebarExpanded = useStudentHomepageStore((state) => state.isSidebarExpanded);
  const setIsSidebarExpanded = useStudentHomepageStore((state) => state.setIsSidebarExpanded);
  const setShowJoinClassModal = useStudentHomepageStore((state) => state.setShowJoinClassModal);
  const showJoinClassModal = useStudentHomepageStore((state) => state.showJoinClassModal);
  const studentHasInvalidEmail = useStudentHomepageStore((state) => state.studentHasInvalidEmail);

  const showTabs = !isWelcomeRoute(pathname);

  const {syncDefaultRoute} = useSyncDefaultRoute();

  const showLoadingSpinner = isStudentLoading || isMaybeRedirectingUser;

  useEffect(() => {
    fetchStudent();
    syncDefaultRoute(classroomId, pathname);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callAction(footerActions.SET_FOOTER_DISPLAY, false);

    return () => {
      callAction(footerActions.SET_FOOTER_DISPLAY, true);
    };
  }, []);

  // Force sidebar to expand/contract when viewport changes to/from mobile
  useEffect(() => {
    if (isSAndDown !== lastIsSAnddown) {
      setIsSidebarExpanded(!isSAndDown);
    }
  }, [isSAndDown, lastIsSAnddown, setIsSidebarExpanded]);

  return (
    <>
      <div
        className={classNames(
          TRANSITION_DURATION_CLASSNAME,
          '-mt-[60px] flex h-[calc(100vh-60px)] flex-col background-subtle m:grid m:grid-cols-[clamp(280px,20vw,320px)_auto] m:grid-rows-[repeat(3,min-content)_1fr] m:transition-[grid-template-columns]',
          {
            'justify-center': showLoadingSpinner,
            'm:grid-cols-[0_auto]!': !isSidebarExpanded,
            [TRANSITION_DELAY_CLASSNAME]: !isSidebarExpanded
          }
        )}
      >
        {showLoadingSpinner ? (
          <LoadingSpinner
            className='m:col-span-full m:row-span-4 m:self-center'
            bottomMarginLevel={0}
            size={5}
          />
        ) : (
          <>
            <Header className='m:col-span-full' classroomId={classroomId} />
            <SidebarWrapperDesktop className='m:row-span-3'>
              <SidebarContent pathname={location.pathname} />
            </SidebarWrapperDesktop>
            <ClassroomInvitations className='mx-x1 mt-x3 s:mx-x2' classroomId={classroomId} />
            {showTabs && (
              <Tabs
                className={classNames('mx-x1 shrink-0 s:mx-x2', {'mt-x3': !hasInvitations})}
                classroomId={classroomId}
                pathname={pathname}
              />
            )}
            <div
              className={classNames(
                'mx-x1 overflow-y-auto rounded-b-3xl background-primary s:mx-x2 m:max-h-full m:self-start',
                {
                  'm:mt-x4': !showTabs && hasInvitations,
                  'row-span-2': !showTabs || !hasInvitations,
                  'row-span-3 mt-x3': !showTabs && !hasInvitations,
                  'rounded-t-3xl': !showTabs
                }
              )}
              // Used to allow intersection observers to observe this as the root scrolling context
              id='tabContentContainer'
              // Unmounts and re-mounts the tab content when the classroom scope or number of classrooms change.
              // This significantly simplifies code since the dashboard tabs care a lot about the classroom scope.
              // See: https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
              key={`tab-content-${classroomId ?? `home-${countClassrooms}`}`}
            >
              {children}
            </div>
            {studentHasInvalidEmail && <InvalidEmailModal />}
            {exceededHardCapLimit && <HardCapStudentErrorModal />}
            {showJoinClassModal && (
              <JoinClassroomModal
                handleClose={() => setShowJoinClassModal(false)}
                onEnrollSuccess={() => {
                  setShowJoinClassModal(false);
                  onClassroomEnrollment(classroomId);
                }}
              />
            )}
          </>
        )}
      </div>
      {!showLoadingSpinner && (
        <SidebarWrapperMobile>
          <SidebarContent pathname={location.pathname} />
        </SidebarWrapperMobile>
      )}
    </>
  );
}
